import {
  DailyExerciseService,
  InitWithAmplify,
  InitEnvironmentVariable,
} from '@PikaDoApp/npm-pikado-logic';
import type {
  DailyExerciseReviewItem,
  NewDailyExercise,
} from '@PikaDoApp/npm-pikado-logic';
import {Amplify} from 'aws-amplify';
import {getEnvironment} from '../../lib/getEnvironment';

/**
 * Service for the Daily Exercises approval/review workflow. Wraps the
 * review-related operations exposed by the shared logic package.
 */
export class DailyExercisesReviewService {
  private readonly dailyExerciseService: DailyExerciseService;

  constructor() {
    // Mirror the initialization used by TestsService so the Content Service
    // Apollo client is configured for the current environment.
    InitWithAmplify(Amplify);
    InitEnvironmentVariable(getEnvironment());
    this.dailyExerciseService = new DailyExerciseService();
  }

  /**
   * Fetches the paginated list of exercises awaiting review (pending status or
   * legacy records without a status field).
   */
  async getExercisesForReview(
    page: number = 1,
    limit: number = 20,
  ): Promise<{
    items: DailyExerciseReviewItem[];
    page: number;
    totalPages: number;
    total: number;
  }> {
    const response = await this.dailyExerciseService.getDailyExercisesForReview(
      page,
      limit,
    );

    return {
      items: response?.data ?? [],
      page: response?.page ?? page,
      totalPages: response?.totalPages ?? 0,
      total: response?.totalElements ?? 0,
    };
  }

  /** Approves an exercise. Reviewer identity is captured server-side. */
  async approve(id: string): Promise<NewDailyExercise> {
    return this.dailyExerciseService.approveDailyExercise(id);
  }

  /** Rejects an exercise with an optional reason. */
  async reject(id: string, reason?: string): Promise<NewDailyExercise> {
    return this.dailyExerciseService.rejectDailyExercise(id, reason);
  }
}
